import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../i18n/LocalizationProvider';
import type { TranslationContent } from '../i18n/translations';

interface Profile {
  id: string;
  name: string;
  dob: string;
  createdAt: string;
  generatedIndex?: number;
}

function generateProfiles(): Profile[] {
  const now = new Date();
  return Array.from({ length: 10 }, (_, index) => ({
    id: `P${(index + 1).toString().padStart(3, '0')}`,
    name: '',
    dob: new Date(now.getFullYear() - 20 - index, index % 12, index + 1)
      .toISOString()
      .slice(0, 10),
    createdAt: now.toISOString().slice(0, 10),
    generatedIndex: index + 1,
  }));
}

export default function TestPage() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const { t } = useTranslation();
  const getDisplayName = useCallback(
    (profile: Profile) =>
      profile.generatedIndex
        ? t('profiles.generatedName', { index: profile.generatedIndex })
        : profile.name,
    [t]
  );
  const historyHeaders = t<TranslationContent['profiles']['history']['table']>('profiles.history.table');
  const slotOptions = t<string[]>('profiles.form.slotOptions');

  useEffect(() => {
    const timeout = setTimeout(() => {
      setProfiles(generateProfiles());
      setLoading(false);
    }, 500);

    return () => clearTimeout(timeout);
  }, []);

  const filteredProfiles = useMemo(() => {
    const keyword = search.toLowerCase();
    return profiles.filter(profile => getDisplayName(profile).toLowerCase().includes(keyword));
  }, [profiles, search, getDisplayName]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const name = formData.get('name') as string;
    const dob = formData.get('dob') as string;

    if (!name || !dob) return;

    const newProfile: Profile = {
      id: `P${(profiles.length + 1).toString().padStart(3, '0')}`,
      name,
      dob,
      createdAt: new Date().toISOString().slice(0, 10),
    };

    setProfiles(prev => [newProfile, ...prev]);
    event.currentTarget.reset();
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadMessage(t('profiles.upload.success', { file: file.name }));
    event.target.value = '';
  };

  if (loading) {
    return (
      <div className="panel" style={{ textAlign: 'center' }}>
        <p className="text-muted">{t('profiles.loading')}</p>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="section-heading">
        <div>
          <span className="badge">{t('profiles.badge')}</span>
          <h2>{t('profiles.title')}</h2>
          <p className="text-muted">{t('profiles.description')}</p>
        </div>
        <div className="metric-card" style={{ margin: 0, padding: '1rem 1.25rem' }}>
          <span>{t('profiles.readyLabel')}</span>
          <strong>{profiles.length}</strong>
        </div>
      </div>

      <div className="panel" style={{ boxShadow: 'none', marginBottom: '2rem' }}>
        <div className="section-heading" style={{ alignItems: 'center' }}>
          <h3>{t('profiles.history.title')}</h3>
          <input
            placeholder={t('profiles.history.searchPlaceholder')}
            value={search}
            onChange={event => setSearch(event.target.value)}
            style={{ maxWidth: 260 }}
          />
        </div>
        <table>
          <thead>
            <tr>
              <th>{historyHeaders.id}</th>
              <th>{historyHeaders.name}</th>
              <th>{historyHeaders.dob}</th>
              <th>{historyHeaders.created}</th>
            </tr>
          </thead>
          <tbody>
            {filteredProfiles.map(profile => (
              <tr key={profile.id}>
                <td>{profile.id}</td>
                <td>{getDisplayName(profile)}</td>
                <td>{profile.dob}</td>
                <td>{profile.createdAt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="metric-grid">
        <div className="panel" style={{ boxShadow: 'none' }}>
          <h3>{t('profiles.upload.title')}</h3>
          <p className="text-muted">{t('profiles.upload.description')}</p>
          <label className="text-muted" style={{ display: 'block' }}>
            <input type="file" accept=".csv" onChange={handleUpload} />
          </label>
          {uploadMessage && <p className="text-muted">{uploadMessage}</p>}
        </div>

        <form className="panel" style={{ boxShadow: 'none' }} onSubmit={handleSubmit}>
          <h3>{t('profiles.form.title')}</h3>
          <div className="form-grid">
            <label>
              <span className="text-muted">{t('profiles.form.nameLabel')}</span>
              <input name="name" placeholder={t('profiles.form.namePlaceholder')} required />
            </label>
            <label>
              <span className="text-muted">{t('profiles.form.dobLabel')}</span>
              <input type="date" name="dob" required />
            </label>
            <label>
              <span className="text-muted">{t('profiles.form.slotLabel')}</span>
              <select name="slot">
                {slotOptions.map(option => (
                  <option key={option}>{option}</option>
                ))}
              </select>
            </label>
            <label>
              <span className="text-muted">{t('profiles.form.notesLabel')}</span>
              <textarea name="notes" rows={3} placeholder={t('profiles.form.notesPlaceholder')} />
            </label>
          </div>
          <button type="submit">{t('profiles.form.submit')}</button>
        </form>
      </div>
    </div>
  );
}
